import { Link } from "react-router-dom";

// Body of the "How to keep track of document expiry dates" article.
// Uses simple, direct typography styled with Tailwind classes to match the site.
const P = ({ children }) => (
  <p className="mt-4 text-[15.5px] leading-[1.75] text-slate-700">{children}</p>
);
const H2 = ({ children, id }) => (
  <h2 id={id} className="mt-14 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
    {children}
  </h2>
);
const UL = ({ children }) => (
  <ul className="mt-4 space-y-2 text-[15.5px] leading-relaxed text-slate-700">{children}</ul>
);
const LI = ({ children }) => (
  <li className="flex gap-3">
    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-greenaccent" />
    <span>{children}</span>
  </li>
);

export default function HowToKeepTrackOfDocumentExpiryDates() {
  return (
    <article className="font-sans">
      <P>
        Missing an expiry date can be surprisingly expensive. A lapsed vehicle insurance policy, an expired permit or an overlooked contract renewal can lead to fines, coverage gaps, missed deadlines and a lot of avoidable stress. The good news: keeping renewals under control does not require complicated software. With a bit of structure — and, later, the right tool — it becomes routine.
      </P>
      <P>
        This guide walks through a simple, practical way to track document expiry and renewal dates, whether you are managing your personal paperwork or a business with dozens of renewals.
      </P>

      <H2 id="why-forgotten">Why expiry dates are easy to forget</H2>
      <P>
        Renewal dates share a few frustrating qualities. They are spread across the year, they live in different places (email inboxes, physical documents, spreadsheets, memory), and they usually only demand attention right before they expire. That combination makes them easy to postpone — and easy to miss.
      </P>
      <P>Common reasons people miss renewals:</P>
      <UL>
        <LI>Renewal notices arrive weeks earlier than the deadline and get buried.</LI>
        <LI>Reminders exist only in someone's head, on a sticky note or on a forgotten calendar entry.</LI>
        <LI>Multiple people share responsibility, so no one clearly owns the deadline.</LI>
        <LI>Documents are stored in different apps, drawers or drives.</LI>
      </UL>

      <H2 id="common-items">Common documents and items with expiry or renewal dates</H2>
      <P>
        Before you can track renewals, it helps to write down what actually needs tracking. Most individuals and businesses end up with a longer list than they expect.
      </P>
      <UL>
        <LI>Vehicle insurance, road tax, pollution and fitness certificates</LI>
        <LI>Driving licences, passports and visas</LI>
        <LI>Commercial permits and business licences</LI>
        <LI>Employee or driver certifications</LI>
        <LI>Annual maintenance contracts (AMC) and equipment warranties</LI>
        <LI>Vendor and service contracts</LI>
        <LI>Domain names, web hosting and SSL certificates</LI>
        <LI>Software and SaaS subscriptions</LI>
        <LI>Insurance policies, memberships and other recurring renewals</LI>
      </UL>
      <P>
        For businesses managing fleets specifically, our{" "}
        <Link to="/fleet-document-expiry-reminder" className="font-semibold text-brand underline-offset-4 hover:underline">
          fleet document expiry reminder guide
        </Link>{" "}
        covers vehicle-specific renewals in more detail.
      </P>

      <H2 id="manual-method">A simple manual method for tracking expiry dates</H2>
      <P>
        You do not need an app to get started. A simple list is often enough for the first few weeks. The goal is to move renewal dates <em>out of your head</em> and into a single, dependable place you actually check.
      </P>
      <UL>
        <LI>Make a single list — a notebook, a spreadsheet or a note in your phone.</LI>
        <LI>For each item, capture: name, category, expiry date, who is responsible, and where the document is kept.</LI>
        <LI>Sort the list by expiry date so the next deadline is always at the top.</LI>
        <LI>Review the list on a fixed day each month, not only when something is about to expire.</LI>
      </UL>
      <P>
        This is enough to prevent most surprises. It is also the foundation that any dedicated app builds on — the difference is who does the remembering.
      </P>

      <H2 id="reminders">Why reminders should be set before the expiry date</H2>
      <P>
        A reminder on the exact expiry date is often too late. Renewals frequently require documents, payments, appointments or approvals, and those take time.
      </P>
      <P>Useful lead times to consider:</P>
      <UL>
        <LI>30 days before expiry for licences, permits and government-issued documents.</LI>
        <LI>14 days before for insurance and vehicle-related renewals.</LI>
        <LI>7 days before for smaller subscriptions and short-term contracts.</LI>
      </UL>
      <P>
        The exact numbers depend on your context, but the principle is the same: give yourself enough runway to complete the renewal calmly rather than in a panic.
      </P>

      <H2 id="keep-document-accessible">Why keeping the related document accessible is useful</H2>
      <P>
        When a reminder fires, the next question is almost always: "Where is the current copy?" If you have to search for the policy PDF, the licence photo or the last invoice, the reminder loses much of its value. A useful setup keeps the document itself linked to the renewal date — either physically (a labelled folder) or digitally (a scanned copy stored next to the renewal record).
      </P>

      <H2 id="business">How businesses can manage many renewal dates</H2>
      <P>
        A single person can get by with a notebook. A business with vehicles, staff certifications, licences, contracts, warranties and subscriptions cannot. As the number of items grows, three things become important:
      </P>
      <UL>
        <LI><strong>Centralisation.</strong> Every renewal lives in one place so nothing is scattered across inboxes and drives.</LI>
        <LI><strong>Clear ownership.</strong> Each renewal has a responsible person, not a shared assumption.</LI>
        <LI><strong>Consistent reminders.</strong> Alerts are sent automatically, with enough lead time, to the right person.</LI>
      </UL>
      <P>
        These are all achievable with a spreadsheet at first. But spreadsheets drift, break and go stale — which is where a dedicated tool starts to earn its place.
      </P>

      <H2 id="dedicated-app">How a dedicated renewal tracking app can help</H2>
      <P>
        A renewal tracking app takes the work out of remembering. Instead of checking a list manually, the app watches every expiry date and notifies you before it lapses. Documents can sit next to their renewal record, and multiple renewals can be viewed in a single dashboard.
      </P>
      <P>
        Good renewal tracking apps typically offer:
      </P>
      <UL>
        <LI>A single dashboard for upcoming, active and expired items.</LI>
        <LI>Email and push reminders with configurable lead times.</LI>
        <LI>Custom categories so you can track anything with a deadline.</LI>
        <LI>Support for individuals and for teams managing many renewals.</LI>
      </UL>

      <H2 id="renewtracker">RenewTracker: one straightforward option</H2>
      <P>
        <Link to="/" className="font-semibold text-brand underline-offset-4 hover:underline">RenewTracker</Link>{" "}
        is a renewal and expiry tracking app built around the ideas above. It centralises renewal and expiry items in one place and sends email and push reminders before deadlines, so you can act in time.
      </P>
      <P>
        RenewTracker currently supports adding renewal/expiry items across categories such as vehicles, employees, equipment, licences, permits, contracts and subscriptions, along with reminders you can configure per item. It is a tracking and reminder tool — it helps you remember, but you still complete each renewal with the relevant authority, insurer or provider. It does not automatically renew documents on your behalf.
      </P>

      <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
        <p className="font-display text-lg font-semibold text-ink">Ready to try it?</p>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          RenewTracker is available now on Google Play. Start tracking your renewal dates in one place.
        </p>
        <a
          href="https://play.google.com/store/apps/details?id=com.renewtracker.app"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="article-play-cta"
          aria-label="Get RenewTracker on Google Play"
          className="mt-5 inline-flex items-center gap-3 rounded-2xl bg-ink px-5 py-3.5 text-white shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
        >
          <span className="grid h-6 w-6 place-items-center">
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path fill="#10B981" d="M6 3.5v17l14-8.5z" />
            </svg>
          </span>
          <span className="text-left leading-tight">
            <span className="block text-[10px] uppercase tracking-widest text-white/60">Get it on</span>
            <span className="block font-display text-base font-semibold">Google Play</span>
          </span>
          <span className="ml-2 rounded-full bg-greenaccent/20 px-2 py-0.5 text-[10px] font-semibold text-greenaccent">Available Now</span>
        </a>
      </div>

      <H2 id="wrap-up">A short checklist to get started</H2>
      <UL>
        <LI>List every item you own or manage that has an expiry or renewal date.</LI>
        <LI>Record the date, the responsible person and where the document lives.</LI>
        <LI>Set a reminder <em>before</em> the expiry date, not on it.</LI>
        <LI>Store the document alongside the renewal record so you can find it fast.</LI>
        <LI>If you are managing more than a handful of renewals, move from a spreadsheet to a dedicated tracking app.</LI>
      </UL>
      <P>
        Renewals are one of those tasks that quietly become expensive when ignored. A few minutes of setup — a list, a reminder or an app — is usually all it takes to stop missing them.
      </P>

      <div className="mt-14 rounded-2xl border border-slate-200 bg-background p-6 text-sm text-slate-600">
        Have a question about tracking renewals?{" "}
        <Link to="/contact" className="font-semibold text-brand hover:underline">
          Get in touch
        </Link>
        {" "}— we're happy to help.
      </div>
    </article>
  );
}
